import { PLOT, plot, shorten } from "./observability-chart";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AdminApiError,
  NOT_AN_ADMIN,
  getObservability,
  getReadingFailures,
  type EventRow,
  type Observability,
  type ReadingFailure,
} from "../../api/observability";
import { download, stampedName, toCsv, type Cell } from "../../api/csv";
import { Loading } from "../Loading";

/**
 * Observability and Admin Analytics.
 *
 * Reconciled from the two Stitch decks of this screen. Panels the designs carry
 * that describe machinery this product does not have (regional audio relays,
 * latency and uptime figures, pass velocity against a benchmark, the candidate
 * override roster) are in `app/DEFERRED.md` with their blockers. Everything
 * drawn below is a count of rows in the live database.
 */

const numbers = new Intl.NumberFormat("en-IN");

function n(value: number): string {
  return numbers.format(value);
}

function clock(at: number): string {
  return new Date(at).toLocaleTimeString("en-IN", { hour12: false });
}

function stamp(at: number): string {
  return new Date(at).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function dayLabel(day: string): string {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, date ?? 1)).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });
}

const SOURCE_LABEL: Record<string, string> = {
  debrief: "Debriefs",
  jd_target: "JD targets",
  mock_reading: "Mock readings",
  mock_session: "Mock sessions",
};

const CONTEXT_TONE: Record<string, string> = {
  identity: "text-primary-fixed-dim",
  candidate: "text-tertiary-fixed-dim",
  search: "text-secondary-fixed-dim",
  skills: "text-secondary-fixed",
  interview: "text-tertiary-fixed",
  commerce: "text-primary-fixed",
};

function Panel({
  children,
  className = "",
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return (
    <section
      className={`bg-surface-container-lowest border border-outline-variant/70 rounded-xl p-5 shadow-sm ${className}`}
    >
      {children}
    </section>
  );
}

function Eyebrow({ children }: { readonly children: React.ReactNode }) {
  return (
    <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider font-semibold">
      {children}
    </span>
  );
}

function Kpi({
  eyebrow,
  caption,
  value,
  beside,
  footLabel,
  footValue,
}: {
  readonly eyebrow: string;
  readonly caption: string;
  readonly value: string;
  readonly beside: string;
  readonly footLabel: string;
  readonly footValue: string;
}) {
  return (
    <Panel className="flex flex-col justify-between gap-space-sm">
      <div className="flex flex-col">
        <Eyebrow>{eyebrow}</Eyebrow>
        <span className="font-label-sm text-label-sm text-outline">{caption}</span>
      </div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-label-md text-[30px] leading-9 font-bold text-on-surface tracking-tight tabular-nums">
          {value}
        </span>
        <span className="font-label-sm text-label-sm text-on-surface-variant">{beside}</span>
      </div>
      <div className="pt-space-sm border-t border-outline-variant/50 flex items-center justify-between gap-2 font-label-sm text-label-sm">
        <span className="text-on-surface-variant">{footLabel}</span>
        <span className="text-on-surface font-semibold tabular-nums">{footValue}</span>
      </div>
    </Panel>
  );
}

function SignupChart({ series }: { readonly series: Observability["signups"] }) {
  const { top, barWidth, bars, total, line, area } = plot(series);
  const { width, height } = PLOT;
  const last = series[series.length - 1];

  return (
    <div className="w-full flex flex-col gap-2 pt-2">
      <div className="h-48 w-full relative flex items-end">
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
          {[top, top / 2, 0].map((mark) => (
            <div key={mark} className="w-full flex items-center gap-2">
              <span className="w-full h-px bg-surface-container-highest" />
              <span className="font-label-sm text-[11px] text-outline tabular-nums">{n(mark)}</span>
            </div>
          ))}
        </div>
        <svg
          className="w-full h-44 overflow-visible"
          preserveAspectRatio="none"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={`Signups a day for the last ${series.length} days, ${n(total)} in total`}
        >
          <defs>
            <linearGradient id="signupArea" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#006c49" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#006c49" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon points={area} fill="url(#signupArea)" />
          <polyline
            points={line}
            fill="none"
            stroke="#006c49"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {bars.map((box, index) => (
            <rect
              key={box.day}
              // Past days in a grey that reads as a bar. The lightest surface
              // grey under the green wash read as a loading skeleton, not data.
              className={index === bars.length - 1 ? "fill-primary" : "fill-outline-variant"}
              x={box.x}
              y={box.y}
              width={barWidth}
              height={Math.max(box.tall, (series[index]?.count ?? 0) > 0 ? 2 : 0)}
              rx="3"
            >
              <title>{`${dayLabel(box.day)}: ${n(series[index]?.count ?? 0)}`}</title>
            </rect>
          ))}
        </svg>
      </div>
      <div className="flex justify-between font-label-sm text-[11px] text-outline px-1">
        <span>{dayLabel(series[0]?.day ?? "")}</span>
        <span className="hidden sm:inline">
          {dayLabel(series[Math.floor(series.length / 3)]?.day ?? "")}
        </span>
        <span className="hidden sm:inline">
          {dayLabel(series[Math.floor((2 * series.length) / 3)]?.day ?? "")}
        </span>
        <span className="text-on-surface font-semibold">
          {dayLabel(last?.day ?? "")} (today: {n(last?.count ?? 0)})
        </span>
      </div>
    </div>
  );
}

function ReachCell({
  label,
  value,
  outOf,
  lead = false,
}: {
  readonly label: string;
  readonly value: number;
  readonly outOf: number;
  readonly lead?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <span className="font-label-sm text-label-sm text-on-surface-variant">{label}</span>
      <span
        className={`font-headline-sm text-headline-sm font-bold tabular-nums ${
          lead ? "text-secondary" : "text-on-surface"
        }`}
      >
        {n(value)}
      </span>
      <span className="font-label-sm text-label-sm text-outline tabular-nums">
        {lead ? "the baseline for the three beside it" : `of ${n(outOf)} people`}
      </span>
    </div>
  );
}

function Meter({
  label,
  value,
  of,
  note,
}: {
  readonly label: string;
  readonly value: number;
  readonly of: number;
  readonly note: string;
}) {
  const share = of === 0 ? 0 : Math.round((value / of) * 100);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between gap-2 font-label-sm text-label-sm">
        <span className="text-on-surface font-medium truncate" title={label}>
          {label}
        </span>
        <span className="text-on-surface-variant tabular-nums whitespace-nowrap">{note}</span>
      </div>
      <div className="w-full h-1.5 bg-surface-container rounded-full overflow-hidden">
        <div className="h-full bg-secondary rounded-full" style={{ width: `${share}%` }} />
      </div>
    </div>
  );
}

function EventConsole({
  rows,
  pollSeconds,
}: {
  readonly rows: readonly EventRow[];
  readonly pollSeconds: number;
}) {
  return (
    <Panel className="flex flex-col gap-space-sm flex-1">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col">
          <h3 className="font-headline-sm text-headline-sm font-semibold text-on-surface">
            Live event stream
          </h3>
          <span className="font-label-sm text-label-sm text-on-surface-variant">
            {rows.length === 0
              ? "The event log, once anything writes to it."
              : `The newest ${rows.length} rows of the event log.`}{" "}
            Polled every {pollSeconds}s, not streamed.
          </span>
        </div>
        <span className="px-2 py-0.5 rounded-full bg-surface-container text-on-surface-variant font-label-sm text-label-sm whitespace-nowrap">
          poll
        </span>
      </div>
      <div className="flex flex-col gap-1.5 p-3 rounded-lg bg-primary-container font-label-sm text-label-sm overflow-y-auto max-h-56">
        {rows.length === 0 ? (
          <span className="text-inverse-primary">No events logged yet.</span>
        ) : (
          rows.map((row) => (
            <div key={row.eventId} className="flex items-start gap-2 leading-relaxed">
              <span className="text-inverse-primary/70 tabular-nums whitespace-nowrap">
                {clock(row.occurredAt)}
              </span>
              <span
                className={`font-semibold uppercase whitespace-nowrap ${
                  CONTEXT_TONE[row.context] ?? "text-inverse-on-surface"
                }`}
              >
                {row.context}
              </span>
              <span className="text-inverse-on-surface truncate" title={row.type}>
                {row.type}
              </span>
              <span
                className="text-inverse-primary/80 whitespace-nowrap"
                title={`subject ${row.subject}, actor ${row.actor}`}
              >
                {shorten(row.subject)} by {shorten(row.actor)}
              </span>
              {!row.published && (
                <span className="ml-auto text-inverse-primary/70 whitespace-nowrap">queued</span>
              )}
            </div>
          ))
        )}
      </div>
      <p className="font-label-sm text-label-sm text-on-surface-variant">
        The event log holds no name, no address and no user id, which is why it can be read here at
        all.
      </p>
    </Panel>
  );
}

function ReadingFailureTable({ rows }: { readonly rows: readonly ReadingFailure[] }) {
  if (rows.length === 0) {
    return (
      <p className="font-body-sm text-body-sm text-on-surface-variant">
        Nothing is stuck. The reader has read everything it was handed.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left font-label-sm text-label-sm">
        <thead>
          <tr className="text-on-surface-variant border-b border-outline-variant/60">
            <th className="py-1.5 pr-3 font-semibold">Person</th>
            <th className="py-1.5 pr-3 font-semibold">Queue</th>
            <th className="py-1.5 pr-3 font-semibold text-right">Attempts</th>
            <th className="py-1.5 font-semibold">Written</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.source}:${row.id}`} className="border-b border-outline-variant/30">
              <td className="py-1.5 pr-3 text-on-surface break-words">{row.email}</td>
              <td className="py-1.5 pr-3 text-on-surface-variant">
                {SOURCE_LABEL[row.source] ?? row.source}
              </td>
              <td className="py-1.5 pr-3 text-on-surface font-semibold tabular-nums text-right">
                {n(row.attempts)}
              </td>
              <td className="py-1.5 text-on-surface-variant tabular-nums whitespace-nowrap">
                {stamp(row.at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ObservabilityScreen() {
  const [data, setData] = useState<Observability | null>(null)
  const [trouble, setTrouble] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState<number | null>(null);
  const [failures, setFailures] = useState<readonly ReadingFailure[] | null>(null);
  const [failureTrouble, setFailureTrouble] = useState<string | null>(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const next = await getObservability(days);
      if (!alive.current) return;
      setData(next);
      setTrouble(null);
    } catch (caught) {
      if (!alive.current) return;
      setTrouble(caught instanceof AdminApiError ? caught.message : "unreachable");
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [days]);

  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  const interval = data?.pollIntervalMs ?? 0;
  useEffect(() => {
    if (interval <= 0) return;
    const timer = setInterval(() => void load(), interval);
    return () => clearInterval(timer);
  }, [interval, load]);

  const reveal = useCallback(async () => {
    setFailureTrouble(null);
    try {
      const answer = await getReadingFailures();
      setFailures(answer.rows);
    } catch (caught) {
      setFailureTrouble(caught instanceof AdminApiError ? caught.message : "unreachable");
    }
  }, []);

  const pins = useMemo(() => data?.models.pins.slice(0, 6) ?? [], [data]);

  const report = useCallback(() => {
    if (data === null) return;
    const rows: (readonly Cell[])[] = [
      ["people", "accounts not deleted", data.people.total],
      ["people", "signed up in the last 30 days", data.people.newIn30Days],
      ["people", "signed in in the last 7 days", data.people.seenIn7Days],
      ["search", "applications", data.pipeline.applications],
      ["search", "applications still live", data.pipeline.active],
      ["search", "applications closed", data.pipeline.closed],
      ["search", "rounds", data.pipeline.rounds],
      ["search", "rounds sat", data.pipeline.roundsSat],
      ["search", "rounds upcoming", data.pipeline.roundsUpcoming],
      ["write ups", "debriefs written", data.writeUps.written],
      ["write ups", "drafts open", data.writeUps.drafts],
      ["write ups", "read by the model", data.writeUps.read],
      ["write ups", "awaiting a reading", data.writeUps.awaiting],
      ["write ups", "competency observations", data.writeUps.observations],
      ["write ups", "distinct skills touched", data.writeUps.skillsTouched],
      ["practice", "mock sessions", data.practice.sessions],
      ["practice", "mock sessions ended", data.practice.sessionsEnded],
      ["practice", "mock readings", data.practice.readings],
      ["practice", "job descriptions", data.practice.jdTargets],
      ["practice", "job descriptions read", data.practice.jdTargetsRead],
      ["reach", "signed up", data.reach.signedUp],
      ["reach", "tracked an application", data.reach.tracked],
      ["reach", "sat a round", data.reach.satARound],
      ["reach", "ran a mock", data.reach.ranAMock],
      ["reach", "wrote it up", data.reach.wroteUp],
      ["reader", "rows still unread after a failure", data.reader.stuck],
      ["reader", "rows read on a retry", data.reader.retried],
      ["reader", "attempts recorded", data.reader.attempts],
      ["reader", "worst row, attempts burned", data.reader.worst],
      ["events", "rows in the event log", data.events.total],
      ["events", "waiting on the outbox", data.events.unpublished],
      ["models", "calls that recorded a pin", data.models.calls],
    ];
    for (const pin of data.models.pins) rows.push(["model pin", pin.pin, pin.calls]);
    for (const one of data.models.bySource) {
      rows.push(["model calls", SOURCE_LABEL[one.source] ?? one.source, one.calls]);
    }
    for (const queue of data.reader.queues) {
      rows.push([
        "reader queue",
        `${SOURCE_LABEL[queue.source] ?? queue.source}, still unread`,
        queue.stuck,
      ]);
    }
    for (const day of data.signups) rows.push(["signups a day", day.day, day.count]);
    download(
      stampedName("prephike-system-report", data.readAt),
      toCsv(["what", "which", "count"], rows),
    );
  }, [data]);

  if (trouble !== null && data === null) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Panel className="flex flex-col gap-space-sm">
          <h1 className="font-headline-md text-headline-md font-bold text-on-surface">
            The console could not read the database
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            {trouble === NOT_AN_ADMIN
              ? "This address is not on the admin allowlist, so nothing is served."
              : `The request failed: ${trouble}.`}
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="self-start px-3 py-1.5 rounded-lg bg-primary text-on-primary font-body-sm text-body-sm font-medium"
          >
            Try again
          </button>
        </Panel>
      </div>
    );
  }

  const pollSeconds = Math.round((data?.pollIntervalMs ?? 0) / 1000);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col gap-space-lg">
      <header className="flex flex-col gap-space-sm">
        <div className="flex flex-wrap items-center gap-space-xs font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">
          <span className="text-on-surface font-semibold">Executive supervision</span>
          <span className="text-outline">/</span>
          <span>Observability</span>
          {data !== null && data.environment !== "" && (
            <span className="px-2 py-0.5 rounded-full bg-surface-container-high text-on-surface font-semibold normal-case tracking-normal">
              {data.environment}
            </span>
          )}
        </div>
        <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-space-md">
          <div className="flex flex-col gap-1 max-w-3xl">
            <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold tracking-tight">
              Observability & Admin Analytics
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Every figure below is a count of rows in the live database, taken when the page last
              read it. Nothing here is sampled, estimated or compared against a benchmark.
            </p>
          </div>
          {/* NOT BEFORE THE READ LANDS. "System report (CSV)" exported a file
              with no rows in it and "Read now" reloaded a first read. */}
          <div className={data === null ? "hidden" : "flex items-center gap-space-sm"}>
            {data !== null && (
              <span className="font-label-sm text-label-sm text-on-surface-variant tabular-nums">
                Read at {clock(data.readAt)}
                {pollSeconds > 0 ? `, re-reads every ${pollSeconds}s` : ""}
              </span>
            )}
            <button
              type="button"
              onClick={report}
              disabled={data === null}
              className="px-3 py-2 rounded-lg bg-surface-container-lowest border border-outline-variant/70 text-on-surface hover:bg-surface-container-high disabled:opacity-60 transition-colors font-body-sm text-body-sm font-medium"
            >
              System report (CSV)
            </button>
            <button
              type="button"
              onClick={() => void load()}
              disabled={busy}
              className="px-3 py-2 rounded-lg bg-surface-container-lowest border border-outline-variant/70 text-on-surface hover:bg-surface-container-high disabled:opacity-60 transition-colors font-body-sm text-body-sm font-medium"
            >
              {busy ? "Reading" : "Read now"}
            </button>
          </div>
        </div>
        {trouble !== null && (
          <p className="font-label-sm text-label-sm text-error">
            The last read failed ({trouble}). The figures below are the ones from
            {data === null ? " nowhere" : ` ${clock(data.readAt)}`}.
          </p>
        )}
      </header>

      {data === null ? (
        <Loading what="Reading the deployment." />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-space-md">
            <Kpi
              eyebrow="People"
              caption="Accounts not deleted"
              value={n(data.people.total)}
              beside={`${n(data.people.newIn30Days)} in the last 30 days`}
              footLabel="Signed in this week"
              footValue={n(data.people.seenIn7Days)}
            />
            <Kpi
              eyebrow="Search pipeline"
              caption="Applications and rounds"
              value={n(data.pipeline.applications)}
              beside={`${n(data.pipeline.active)} still live`}
              footLabel="Rounds sat"
              footValue={`${n(data.pipeline.roundsSat)} of ${n(data.pipeline.rounds)}`}
            />
            <Kpi
              eyebrow="Written up"
              caption="Debriefs and what came out of them"
              value={n(data.writeUps.written)}
              beside={`${n(data.writeUps.read)} read by the model`}
              footLabel="Competency observations"
              footValue={n(data.writeUps.observations)}
            />
            <Kpi
              eyebrow="Model work"
              caption="Calls that recorded which model ran"
              value={n(data.models.calls)}
              beside={`${n(data.models.pins.length)} model ${
                data.models.pins.length === 1 ? "pin" : "pins"
              }`}
              footLabel="Reads the model is stuck on"
              footValue={n(data.reader.stuck)}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-md">
            <Panel className="lg:col-span-7 flex flex-col justify-between gap-space-md">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-space-sm">
                <div className="flex flex-col">
                  <h2 className="font-headline-sm text-headline-sm font-semibold text-on-surface">
                    Signups, a day at a time
                  </h2>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    Accounts created over the last {data.signups.length} days, counted from
                    identity. Days with nobody are drawn flat, not skipped.
                  </p>
                </div>
                <div className="flex items-center gap-1.5 self-start">
                  {data.windows.map((span) => (
                    <button
                      key={span}
                      type="button"
                      aria-pressed={data.windowDays === span}
                      onClick={() => setDays(span)}
                      className={[
                        "px-2.5 py-1 rounded-lg font-label-sm text-label-sm border transition-colors",
                        data.windowDays === span
                          ? "bg-primary text-on-primary border-primary font-semibold"
                          : "bg-surface-container text-on-surface-variant border-outline-variant hover:text-on-surface",
                      ].join(" ")}
                    >
                      {span} days
                    </button>
                  ))}
                  <span className="px-2 py-0.5 rounded-full bg-surface-container text-on-surface-variant font-label-sm text-label-sm whitespace-nowrap">
                    {n(data.signups.reduce((sum, one) => sum + one.count, 0))} in the window
                  </span>
                </div>
              </div>

              <SignupChart series={data.signups} />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-space-md p-3 rounded-xl bg-surface-container-low">
                <ReachCell
                  label="Signed up"
                  value={data.reach.signedUp}
                  outOf={data.reach.signedUp}
                  lead
                />
                <ReachCell
                  label="Tracked an application"
                  value={data.reach.tracked}
                  outOf={data.reach.signedUp}
                />
                <ReachCell
                  label="Sat a round"
                  value={data.reach.satARound}
                  outOf={data.reach.signedUp}
                />
                <ReachCell
                  label="Wrote it up"
                  value={data.reach.wroteUp}
                  outOf={data.reach.signedUp}
                />
              </div>

              <div className="flex flex-wrap items-center gap-x-space-lg gap-y-2 font-label-sm text-label-sm text-on-surface-variant">
                <span>
                  Mock sessions:{" "}
                  <span className="text-on-surface font-semibold tabular-nums">
                    {n(data.practice.sessions)}
                  </span>{" "}
                  ({n(data.practice.sessionsEnded)} ended, {n(data.practice.readings)} read)
                </span>
                <span>
                  JD targets:{" "}
                  <span className="text-on-surface font-semibold tabular-nums">
                    {n(data.practice.jdTargets)}
                  </span>{" "}
                  ({n(data.practice.jdTargetsRead)} read)
                </span>
                <span>
                  Drafts open:{" "}
                  <span className="text-on-surface font-semibold tabular-nums">
                    {n(data.writeUps.drafts)}
                  </span>
                </span>
              </div>
            </Panel>

            <div className="lg:col-span-5 flex flex-col gap-space-md">
              <Panel className="flex flex-col gap-space-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col">
                    <h3 className="font-headline-sm text-headline-sm font-semibold text-on-surface">
                      Which model actually ran
                    </h3>
                    <span className="font-label-sm text-label-sm text-on-surface-variant">
                      The pin each row recorded, counted across debriefs, JD targets and mocks.
                    </span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full bg-surface-container text-on-surface-variant font-label-sm text-label-sm whitespace-nowrap">
                    {n(data.models.calls)} calls
                  </span>
                </div>
                <div className="flex flex-col gap-2.5 pt-1">
                  {pins.length === 0 ? (
                    <p className="font-body-sm text-body-sm text-on-surface-variant">
                      No row has recorded a model pin yet. Nothing has been read.
                    </p>
                  ) : (
                    pins.map((pin) => (
                      <Meter
                        key={pin.pin}
                        label={pin.pin}
                        value={pin.calls}
                        of={data.models.calls}
                        note={`${n(pin.calls)} calls`}
                      />
                    ))
                  )}
                </div>
                {data.models.bySource.length > 0 && (
                  <div className="flex flex-wrap gap-x-space-md gap-y-1 pt-space-xs border-t border-outline-variant/50 font-label-sm text-label-sm text-on-surface-variant">
                    {data.models.bySource.map((one) => (
                      <span key={one.source}>
                        {SOURCE_LABEL[one.source] ?? one.source}:{" "}
                        <span className="text-on-surface font-semibold tabular-nums">
                          {n(one.calls)}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </Panel>

              <EventConsole rows={data.events.recent} pollSeconds={pollSeconds} />
            </div>
          </div>

          <Panel className="flex flex-col gap-space-md">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-space-sm pb-space-xs border-b border-outline-variant/50">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-headline-md text-headline-md font-bold text-on-surface tracking-tight">
                    Reader health
                  </h2>
                  <span className="px-2 py-0.5 rounded-full bg-surface-container-highest text-on-surface-variant font-label-sm text-label-sm">
                    {n(data.reader.attempts)} attempts recorded
                  </span>
                </div>
                <p className="font-body-md text-body-md text-on-surface-variant">
                  A debrief or a JD target the model could not read counts an attempt and is tried
                  again. A row that keeps counting is one nothing can read.
                </p>
              </div>
              <div className="flex items-baseline gap-space-md font-label-sm text-label-sm text-on-surface-variant">
                <span>
                  Event rows:{" "}
                  <span className="text-on-surface font-semibold tabular-nums">
                    {n(data.events.total)}
                  </span>
                </span>
                <span>
                  Waiting on the outbox:{" "}
                  <span className="text-on-surface font-semibold tabular-nums">
                    {n(data.events.unpublished)}
                  </span>
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-md">
              <div className="lg:col-span-7 flex flex-col gap-space-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-sm">
                  {data.reader.queues.map((queue) => (
                    <div
                      key={queue.source}
                      className="p-3 rounded-xl bg-surface-container-low flex flex-col gap-1"
                    >
                      <Eyebrow>{SOURCE_LABEL[queue.source] ?? queue.source}</Eyebrow>
                      <div className="flex items-baseline gap-2">
                        <span
                          className={`font-headline-md text-headline-md font-bold tabular-nums ${
                            queue.stuck > 0 ? "text-error" : "text-on-surface"
                          }`}
                        >
                          {n(queue.stuck)}
                        </span>
                        <span className="font-label-sm text-label-sm text-on-surface-variant">
                          still unread after a failure
                        </span>
                      </div>
                      <span className="font-label-sm text-label-sm text-outline tabular-nums">
                        {n(queue.retried)} read on a retry, worst row {n(queue.worst)} attempts
                      </span>
                    </div>
                  ))}
                </div>

                {data.reader.histogram.length > 0 && (
                  <div className="flex flex-col gap-2 pt-space-xs">
                    <Eyebrow>Stuck rows by attempts burned</Eyebrow>
                    {data.reader.histogram.map((bucket) => (
                      <Meter
                        key={bucket.attempts}
                        label={`${n(bucket.attempts)} ${
                          bucket.attempts === 1 ? "attempt" : "attempts"
                        }`}
                        value={bucket.stuck}
                        of={data.reader.stuck}
                        note={`${n(bucket.stuck)} ${bucket.stuck === 1 ? "row" : "rows"}`}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="lg:col-span-5 flex flex-col gap-space-sm p-4 rounded-xl bg-surface-container-low">
                <Eyebrow>The rows behind those counts</Eyebrow>
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  Everything above this box is a count and names nobody. Opening the list names the
                  candidates whose rows are stuck, and writes one audit row for each of them under
                  your address. Open it to debug the reader, not to browse.
                </p>
                {failures === null ? (
                  <button
                    type="button"
                    onClick={() => void reveal()}
                    disabled={data.reader.stuck === 0}
                    className="self-start px-3 py-2 rounded-lg bg-primary text-on-primary hover:bg-surface-tint disabled:opacity-50 disabled:hover:bg-primary transition-colors font-body-sm text-body-sm font-medium"
                  >
                    {data.reader.stuck === 0
                      ? "Nothing is stuck"
                      : `Name the ${n(data.reader.stuck)} stuck ${
                          data.reader.stuck === 1 ? "row" : "rows"
                        }`}
                  </button>
                ) : (
                  <>
                    <p className="font-label-sm text-label-sm text-on-surface-variant">
                      Written to the audit ledger. Visible on Audit and Security.
                    </p>
                    <ReadingFailureTable rows={failures} />
                  </>
                )}
                {failureTrouble !== null && (
                  <p className="font-label-sm text-label-sm text-error">
                    The list could not be read ({failureTrouble}).
                  </p>
                )}
              </div>
            </div>
          </Panel>


          <div className="w-full rounded-xl bg-surface-container-low p-space-md flex flex-col lg:flex-row lg:items-center justify-between gap-space-sm font-label-sm text-label-sm text-on-surface-variant">
            <span>
              Counted from the one database the candidate app writes. No copy, no sync, no second
              source of truth.
            </span>
            <span className="tabular-nums">
              Read at {stamp(data.readAt)}
              {data.environment === "" ? "" : `, ${data.environment}`}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
